import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Plus, Trash2, Users, AlertCircle, Palette, Edit2, Eye, EyeOff, ChevronDown, ChevronUp } from 'lucide-react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { FullPageLoader, LoadingSpinner } from '../components/LoadingSpinner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAPBOX_TOKEN = 'pk.eyJ1IjoiYWxtb25kbWlsa3MiLCJhIjoiY21nZjczZDFrMDU3ODJpb3J1bXpmdTJ6MiJ9.WCVmPa68SdgT7PH4mGlB-w';
mapboxgl.accessToken = MAPBOX_TOKEN;

const GROUP_COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

export default function Allocate() {
  const [trip, setTrip] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newGroupName, setNewGroupName] = useState('');
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingGroupName, setEditingGroupName] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState(new Set()); // Feature 2: Collapsible groups
  const [hiddenGroups, setHiddenGroups] = useState(new Set()); // Feature 4: Hide groups from map
  const [allocating, setAllocating] = useState(false); // Loading state for auto-allocate
  const [mapStyle, setMapStyle] = useState('mapbox://styles/mapbox/streets-v12');
  const [selectedCarLicenseTypes, setSelectedCarLicenseTypes] = useState(new Set()); // Car/License type filter
  const [showCarLicenseFilter, setShowCarLicenseFilter] = useState(false); // Toggle filter visibility
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markers = useRef([]);
  const { tripId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();

  // OPTIMIZATION: Already using Promise.all - good!
  useEffect(() => {
    fetchData();
  }, [tripId]);

  useEffect(() => {
    if (participants.length > 0 && participants.some(p => p.lat && p.lng) && !map.current) {
      initializeMap();
    } else if (map.current && participants.length > 0) {
      updateMarkers();
    }
  }, [participants, groups, hiddenGroups, selectedCarLicenseTypes]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tripRes, partsRes, groupsRes] = await Promise.all([
        axios.get(`${API_URL}/trips/${tripId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/trips/${tripId}/participants`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/trips/${tripId}/groups`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setTrip(tripRes.data);
      setParticipants(partsRes.data);
      setGroups(groupsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const initializeMap = () => {
    if (map.current) return;

    const participantsWithCoords = participants.filter(p => p.lat && p.lng);
    if (participantsWithCoords.length === 0) return;

    const bounds = new mapboxgl.LngLatBounds();
    participantsWithCoords.forEach(p => bounds.extend([p.lng, p.lat]));

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapStyle,
      bounds: bounds,
      fitBoundsOptions: { padding: 50 }
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
    updateMarkers();
  };

  const updateMarkers = () => {
    if (!map.current) return;

    markers.current.forEach(marker => marker.remove());
    markers.current = [];

    const participantsWithCoords = participants.filter(p => p.lat && p.lng);

    participantsWithCoords.forEach(p => {
      const group = groups.find(g => g.id === p.group_id);
      const color = group ? group.color : '#94a3b8';
      
      // Feature 4: Skip hidden groups
      if (p.group_id && hiddenGroups.has(p.group_id)) {
        return;
      }
      
      // Skip if doesn't match car/license type filter
      if (!isParticipantVisible(p)) {
        return;
      }
      
      const name = p.role === 'driver'
        ? `${p.driver_first_name || ''} ${p.driver_last_name || ''}`.trim()
        : `${p.passenger_first_name || ''} ${p.passenger_last_name || ''}`.trim();

      const el = document.createElement('div');
      // Feature Update: Increased marker size to 22px (20% bigger than 18px)
      el.style.width = '22px';
      el.style.height = '22px';
      el.style.borderRadius = '50%';
      el.style.border = '2px solid white';
      el.style.backgroundColor = color;
      el.style.cursor = 'pointer';
      el.style.display = 'flex';
      el.style.alignItems = 'center';
      el.style.justifyContent = 'center';
      el.style.color = 'white';
      el.style.fontWeight = 'bold';
      el.style.fontSize = '10px';
      el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';

      if (p.role === 'driver') {
        el.textContent = p.capacity || 0;
      }

      const popup = new mapboxgl.Popup({ offset: 15, closeButton: false, closeOnClick: false })
        .setHTML(`
          <div style="font-family: Inter, sans-serif; padding: 4px;">
            <p style="font-weight: bold; margin-bottom: 4px; font-size: 13px;">${name || 'N/A'}</p>
            <p style="font-size: 11px; color: #64748b; margin: 2px 0;"><strong>Role:</strong> ${p.role}</p>
            <p style="font-size: 11px; color: #64748b; margin: 2px 0;"><strong>Group:</strong> ${group ? group.name : 'Unallocated'}</p>
            <p style="font-size: 11px; color: #64748b; margin: 2px 0;"><strong>Phone:</strong> ${p.phone}</p>
            ${p.car_license_type ? `<p style="font-size: 11px; color: #64748b; margin: 2px 0;"><strong>Car/License:</strong> ${p.car_license_type}</p>` : ''}
            ${p.notes ? `<p style="font-size: 11px; color: #64748b; margin: 2px 0;"><strong>Notes:</strong> ${p.notes}</p>` : ''}
          </div>
        `);

      const marker = new mapboxgl.Marker(el)
        .setLngLat([p.lng, p.lat])
        .setPopup(popup)
        .addTo(map.current);

      el.addEventListener('mouseenter', () => popup.addTo(map.current));
      el.addEventListener('mouseleave', () => popup.remove());
      
      // Feature 2: Click to scroll to group
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        if (p.group_id) {
          scrollToGroup(p.group_id);
        }
      });

      markers.current.push(marker);
    });
  };

  const toggleMapStyle = () => {
    const newStyle = mapStyle === 'mapbox://styles/mapbox/streets-v12' 
      ? 'mapbox://styles/mapbox/dark-v11' 
      : 'mapbox://styles/mapbox/streets-v12';
    
    setMapStyle(newStyle);
    
    if (map.current) {
      map.current.setStyle(newStyle);
      map.current.once('style.load', () => updateMarkers());
    }
  };

  const createGroup = async (e) => {
    e.preventDefault();
    try {
      const colorIndex = groups.length % GROUP_COLORS.length;
      const response = await axios.post(
        `${API_URL}/trips/${tripId}/groups`,
        { name: newGroupName, color: GROUP_COLORS[colorIndex] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Group created!');
      setGroups([...groups, response.data]);
      setNewGroupName('');
      setShowGroupDialog(false);
    } catch (error) {
      toast.error('Failed to create group');
    }
  };

  const deleteGroup = async (groupId) => {
    if (!window.confirm('Delete this group? Participants will become unallocated.')) return;
    
    try {
      await axios.delete(`${API_URL}/groups/${groupId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Group deleted');
      setGroups(groups.filter(g => g.id !== groupId));
      setParticipants(participants.map(p => p.group_id === groupId ? {...p, group_id: null} : p));
    } catch (error) {
      toast.error('Failed to delete group');
    }
  };

  const updateParticipantGroup = async (participantId, groupId) => {
    try {
      await axios.put(
        `${API_URL}/participants/${participantId}`,
        { group_id: groupId || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setParticipants(participants.map(p => 
        p.id === participantId ? {...p, group_id: groupId || null} : p
      ));
      toast.success('Participant moved');
    } catch (error) {
      toast.error('Failed to update participant');
    }
  };

  const startEditingGroup = (group) => {
    setEditingGroupId(group.id);
    setEditingGroupName(group.name);
  };

  const updateGroupName = async (groupId) => {
    if (!editingGroupName.trim()) {
      toast.error('Group name cannot be empty');
      return;
    }

    try {
      const response = await axios.put(
        `${API_URL}/groups/${groupId}`,
        { name: editingGroupName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setGroups(groups.map(g => g.id === groupId ? response.data : g));
      setEditingGroupId(null);
      setEditingGroupName('');
      toast.success('Group name updated');
    } catch (error) {
      toast.error('Failed to update group name');
    }
  };

  const updateGroupColor = async (groupId, newColor) => {
    try {
      const response = await axios.put(
        `${API_URL}/groups/${groupId}`,
        { color: newColor },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setGroups(groups.map(g => g.id === groupId ? response.data : g));
      toast.success('Group color updated');
    } catch (error) {
      toast.error('Failed to update group color');
    }
  };

  const cancelEditingGroup = () => {
    setEditingGroupId(null);
    setEditingGroupName('');
  };

  // Feature 2: Toggle group collapse
  const toggleGroupCollapse = (groupId) => {
    const newCollapsed = new Set(collapsedGroups);
    if (newCollapsed.has(groupId)) {
      newCollapsed.delete(groupId);
    } else {
      newCollapsed.add(groupId);
    }
    setCollapsedGroups(newCollapsed);
  };

  // Feature 4: Toggle group visibility on map
  const toggleGroupVisibility = (groupId) => {
    const newHidden = new Set(hiddenGroups);
    if (newHidden.has(groupId)) {
      newHidden.delete(groupId);
    } else {
      newHidden.add(groupId);
    }
    setHiddenGroups(newHidden);
  };

  const autoAllocate = async () => {
    setAllocating(true);
    try {
      const response = await axios.post(
        `${API_URL}/allocate`,
        { trip_id: tripId, algorithm: 'nearest_driver' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const summary = response.data.summary;
      toast.success(
        `Auto-allocation complete! Created ${summary.groups_created} groups, assigned ${summary.passengers_assigned} passengers. ${summary.passengers_unassigned > 0 ? `${summary.passengers_unassigned} passengers unassigned.` : 'All passengers assigned!'}`,
        { duration: 5000 }
      );
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Auto-allocation failed', { duration: 5000 });
    } finally {
      setAllocating(false);
    }
  };

  // Feature 2: Scroll to group when marker is clicked
  const scrollToGroup = (groupId) => {
    const groupElement = document.querySelector(`[data-testid="group-${groupId}"]`);
    if (groupElement) {
      // Scroll to the group with smooth animation
      groupElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'nearest'
      });
      
      // Add temporary highlight animation
      groupElement.style.transition = 'all 0.3s ease';
      groupElement.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.3)';
      groupElement.style.borderColor = 'rgba(59, 130, 246, 0.5)';
      
      setTimeout(() => {
        groupElement.style.boxShadow = '';
        groupElement.style.borderColor = '';
      }, 1500);
    }
  };

  // OPTIMIZATION: Memoize expensive calculations
  const { unallocated, drivers, passengers } = useMemo(() => {
    return {
      unallocated: participants.filter(p => !p.group_id),
      drivers: participants.filter(p => p.role === 'driver'),
      passengers: participants.filter(p => p.role === 'passenger')
    };
  }, [participants]);

  // Get unique car/license types for filter
  const uniqueCarLicenseTypes = useMemo(() => {
    const types = new Set();
    participants.forEach(p => {
      if (p.car_license_type && p.car_license_type.trim()) {
        types.add(p.car_license_type.trim());
      }
    });
    return Array.from(types).sort();
  }, [participants]);

  const getGroupWarnings = useMemo(() => (group) => {
    const groupParticipants = participants.filter(p => p.group_id === group.id);
    const groupDrivers = groupParticipants.filter(p => p.role === 'driver');
    const groupPassengers = groupParticipants.filter(p => p.role === 'passenger');
    const totalCapacity = groupDrivers.reduce((sum, d) => sum + (d.capacity || 0), 0);
    
    const warnings = [];
    if (groupDrivers.length === 0) warnings.push('No driver in this group');
    if (groupPassengers.length > totalCapacity) warnings.push(`Over capacity: ${groupPassengers.length} passengers, ${totalCapacity} seats`);
    
    return warnings;
  }, [participants]);

  // Toggle car/license type selection
  const toggleCarLicenseType = (type) => {
    const newSelected = new Set(selectedCarLicenseTypes);
    if (newSelected.has(type)) {
      newSelected.delete(type);
    } else {
      newSelected.add(type);
    }
    setSelectedCarLicenseTypes(newSelected);
  };

  // Select all car/license types
  const selectAllCarLicenseTypes = () => {
    setSelectedCarLicenseTypes(new Set(uniqueCarLicenseTypes));
  };

  // Clear all car/license types
  const clearAllCarLicenseTypes = () => {
    setSelectedCarLicenseTypes(new Set());
  };

  // Check if participant should be visible based on filters
  const isParticipantVisible = (participant) => {
    // If no car/license types are selected, show all
    if (selectedCarLicenseTypes.size === 0) return true;
    
    // If participant has no car/license type, hide if filter is active
    if (!participant.car_license_type || !participant.car_license_type.trim()) {
      return false;
    }
    
    // Check if participant's type is in selected types
    return selectedCarLicenseTypes.has(participant.car_license_type.trim());
  };

  if (loading) {
    return <FullPageLoader text="Loading groups..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                {trip?.name || 'Trip'}
              </h1>
              <p className="text-sm text-slate-600 mt-1">Step 3: Allocate Groups</p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => navigate(`/trip/${tripId}/map`)}
                data-testid="back-to-map"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={() => navigate(`/trip/${tripId}/sms`)}
                data-testid="next-to-sms"
              >
                Next: SMS
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Actions */}
        <div className="mb-6 flex gap-3">
          <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
            <DialogTrigger asChild>
              <Button data-testid="create-group-button">
                <Plus className="w-4 h-4 mr-2" />
                New Group
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Group</DialogTitle>
              </DialogHeader>
              <form onSubmit={createGroup} className="space-y-4">
                <div>
                  <Label htmlFor="group-name">Group Name</Label>
                  <Input
                    id="group-name"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    placeholder="e.g., Group 1"
                    required
                    data-testid="group-name-input"
                    className="mt-1"
                  />
                </div>
                <Button type="submit" className="w-full">Create Group</Button>
              </form>
            </DialogContent>
          </Dialog>
          
          <Button
            variant="outline"
            onClick={autoAllocate}
            disabled={drivers.length === 0 || allocating}
            data-testid="auto-allocate-button"
          >
            {allocating ? (
              <LoadingSpinner size="sm" />
            ) : (
              <>
                <Users className="w-4 h-4 mr-2" />
                Auto-Allocate
              </>
            )}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Map (Sticky) */}
          <div className="lg:sticky lg:top-4 lg:self-start">
            {/* Map Controls */}
            <div className="mb-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-slate-900">Map View</h3>
              <Button variant="outline" size="sm" onClick={toggleMapStyle}>
                {mapStyle === 'mapbox://styles/mapbox/streets-v12' ? '🌙 Dark' : '☀️ Light'}
              </Button>
            </div>

            {/* Map */}
            {participants.some(p => p.lat && p.lng) ? (
              <div
                ref={mapContainer}
                className="w-full h-[500px] rounded-lg shadow-lg mb-4"
                data-testid="allocate-map"
              />
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-slate-600">Geocode addresses on the Map page first</p>
                </CardContent>
              </Card>
            )}

            {/* Legend */}
            <div className="p-4 bg-white rounded-lg border border-slate-200">
              <p className="text-sm font-medium text-slate-700 mb-2">Legend:</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li>• Colors indicate group assignment</li>
                <li>• Gray markers are unallocated</li>
                <li>• Hover over markers for details</li>
              </ul>
            </div>
          </div>

          {/* Right: Groups */}
          <div className="space-y-4">
            {/* Car/License Type Filter */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Filter by Car/License Type</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowCarLicenseFilter(!showCarLicenseFilter)}
                  >
                    {showCarLicenseFilter ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </Button>
                </div>
              </CardHeader>
              {showCarLicenseFilter && (
                <CardContent>
                  {uniqueCarLicenseTypes.length === 0 ? (
                    <p className="text-sm text-slate-500">
                      No car/license types found. Add car/license types to participants in Step 1 to enable filtering.
                    </p>
                  ) : (
                    <>
                      <div className="flex gap-2 mb-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={selectAllCarLicenseTypes}
                        >
                          Select All
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={clearAllCarLicenseTypes}
                        >
                          Clear All
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {uniqueCarLicenseTypes.map((type) => (
                          <label
                            key={type}
                            className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-2 rounded"
                          >
                            <input
                              type="checkbox"
                              checked={selectedCarLicenseTypes.has(type)}
                              onChange={() => toggleCarLicenseType(type)}
                              className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                            />
                            <span className="text-sm text-slate-700">{type}</span>
                          </label>
                        ))}
                      </div>
                      {selectedCarLicenseTypes.size > 0 && (
                        <p className="text-xs text-slate-500 mt-3">
                          Showing participants with: {Array.from(selectedCarLicenseTypes).join(', ')}
                        </p>
                      )}
                    </>
                  )}
                </CardContent>
              )}
            </Card>

            {/* Groups */}
            {groups.map((group) => {
              const groupParticipants = participants.filter(p => p.group_id === group.id);
              const warnings = getGroupWarnings(group);
              
              return (
                <Card key={group.id} className={warnings.length > 0 ? 'border-red-300' : ''} data-testid={`group-${group.id}`}>
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3 flex-1">
                        {/* Color Picker */}
                        <input
                          type="color"
                          value={group.color}
                          onChange={(e) => updateGroupColor(group.id, e.target.value)}
                          className="w-8 h-8 rounded-full cursor-pointer border-2 border-white shadow-sm"
                          title="Change group color"
                        />
                        
                        {/* Editable Group Name */}
                        {editingGroupId === group.id ? (
                          <div className="flex items-center gap-2 flex-1">
                            <Input
                              value={editingGroupName}
                              onChange={(e) => setEditingGroupName(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') updateGroupName(group.id);
                                if (e.key === 'Escape') cancelEditingGroup();
                              }}
                              className="h-8"
                              autoFocus
                            />
                            <Button size="sm" onClick={() => updateGroupName(group.id)}>Save</Button>
                            <Button size="sm" variant="ghost" onClick={cancelEditingGroup}>Cancel</Button>
                          </div>
                        ) : (
                          <CardTitle 
                            className="cursor-pointer hover:text-blue-600"
                            onClick={() => startEditingGroup(group)}
                            title="Click to edit name"
                          >
                            {group.name}
                          </CardTitle>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Feature 4: Hide from map */}
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => toggleGroupVisibility(group.id)}
                          title={hiddenGroups.has(group.id) ? "Show on map" : "Hide from map"}
                        >
                          {hiddenGroups.has(group.id) ? (
                            <EyeOff className="w-4 h-4 text-slate-400" />
                          ) : (
                            <Eye className="w-4 h-4 text-slate-600" />
                          )}
                        </Button>
                        {/* Feature 2: Collapse/Expand */}
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => toggleGroupCollapse(group.id)}
                          title={collapsedGroups.has(group.id) ? "Expand" : "Collapse"}
                        >
                          {collapsedGroups.has(group.id) ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronUp className="w-4 h-4" />
                          )}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteGroup(group.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                    {warnings.length > 0 && (
                      <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        <span>{warnings.join(', ')}</span>
                      </div>
                    )}
                  </CardHeader>
                  {/* Feature 2: Conditionally render based on collapse state */}
                  {!collapsedGroups.has(group.id) && (
                    <CardContent>
                      {(() => {
                        const visibleParticipants = groupParticipants.filter(isParticipantVisible);
                        return visibleParticipants.length === 0 ? (
                          <p className="text-sm text-slate-500">
                            {groupParticipants.length === 0 ? 'No participants yet' : 'No participants match the selected car/license types'}
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {visibleParticipants.map((p) => {
                            const name = p.role === 'driver'
                              ? `${p.driver_first_name || ''} ${p.driver_last_name || ''}`.trim()
                              : `${p.passenger_first_name || ''} ${p.passenger_last_name || ''}`.trim();
                            
                            return (
                              <div key={p.id} className="flex items-center justify-between text-sm py-2 border-b last:border-0">
                                <div className="flex-1">
                                  <span className="font-medium">{name}</span>
                                  <span className="text-slate-500 ml-2">({p.role})</span>
                                  {p.role === 'driver' && <span className="text-blue-600 ml-2">Seats: {p.capacity || 0}</span>}
                                  {p.car_license_type && <span className="text-purple-600 ml-2 text-xs">{p.car_license_type}</span>}
                                </div>
                                <Select
                                  value={p.group_id || 'unallocated'}
                                  onValueChange={(value) => updateParticipantGroup(p.id, value === 'unallocated' ? null : value)}
                                >
                                  <SelectTrigger className="w-[180px]">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="unallocated">Unallocated</SelectItem>
                                    {groups.map(g => (
                                      <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            );
                            })}
                          </div>
                        );
                      })()}
                    </CardContent>
                  )}
                </Card>
              );
            })}

            {/* Unallocated */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-slate-400"></div>
                  Unallocated ({unallocated.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const visibleUnallocated = unallocated.filter(isParticipantVisible);
                  return visibleUnallocated.length === 0 ? (
                    <p className="text-sm text-slate-500">
                      {unallocated.length === 0 ? 'All participants allocated!' : 'No unallocated participants match the selected car/license types'}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {visibleUnallocated.map((p) => {
                        const name = p.role === 'driver'
                          ? `${p.driver_first_name || ''} ${p.driver_last_name || ''}`.trim()
                          : `${p.passenger_first_name || ''} ${p.passenger_last_name || ''}`.trim();
                        
                        return (
                          <div key={p.id} className="flex items-center justify-between text-sm py-2 border-b last:border-0">
                            <div className="flex-1">
                              <span className="font-medium">{name}</span>
                              <span className="text-slate-500 ml-2">({p.role})</span>
                              {p.role === 'driver' && <span className="text-blue-600 ml-2">Seats: {p.capacity || 0}</span>}
                              {p.car_license_type && <span className="text-purple-600 ml-2 text-xs">{p.car_license_type}</span>}
                            </div>
                            <Select
                              value="unallocated"
                              onValueChange={(value) => updateParticipantGroup(p.id, value === 'unallocated' ? null : value)}
                            >
                              <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder="Assign to group" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="unallocated">Unallocated</SelectItem>
                                {groups.map(g => (
                                  <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="fixed bottom-0 w-full py-4 text-center bg-white/80 backdrop-blur border-t border-slate-200">
        <a
          href="https://donate.stripe.com/5kQ6oH99f72ze6OcIK4ZG00"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-slate-400 hover:text-slate-600"
        >
          Support this project
        </a>
      </footer>
    </div>
  );
}
